import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, DialogActions, DialogContent,
  Grid, Stack, TextField, Typography,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RateGrid from '../../../components/shared/pricing/RateGrid';
import { fmtDollar, fmtPricingPct } from '../../../components/shared/pricing/pricingUtils';
import loansApi from '../../../services/loansApi';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';

const ADVISOR_DISCLAIMER =
  'Illustrative pricing recommendation — not an underwriting approval or AUS finding.';

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : null);

const parseTargetPrice = (value) => {
  if (value === '' || value == null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const buildAdvisorOptions = ({
  lockDuration,
  compensationType,
  selectedFamilies,
  selectedTerms,
  confirmedFico,
  targetPrice,
}) => ({
  lockTerm: lockDuration,
  compensationType,
  productFamilies: selectedFamilies,
  terms: selectedTerms,
  confirmedFico: confirmedFico ? Number(confirmedFico) : undefined,
  targetPrice: parseTargetPrice(targetPrice),
});

const MismoAdvisorStep = ({
  selectedFile,
  lockDuration,
  compensationType,
  selectedFamilies,
  selectedTerms,
  bpcCalcMethod,
  bpcValue,
  onAgree,
  onDisagree,
  onBack,
  loading: parentLoading,
}) => {
  const [previewMismoAdvisor] = loansApi.endpoints.previewMismoAdvisor.useMutation();
  const [confirmedFico, setConfirmedFico] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [disagreeMode, setDisagreeMode] = useState(false);
  const [overrideRate, setOverrideRate] = useState(null);
  const [showAllRates, setShowAllRates] = useState(false);

  const needsFico = preview?.missingFields?.includes('representativeFico');
  const recommendation = preview?.recommendation;
  const analysis = preview?.analysis;
  const hasRecommendation = recommendation?.hasRecommendation;
  const pricingQuotedAt = preview?.pricingResponse?.quotedAt;

  const ficoValid = !needsFico || (
    confirmedFico
    && Number(confirmedFico) >= 300
    && Number(confirmedFico) <= 850
  );

  const advisorOptions = useMemo(
    () => buildAdvisorOptions({
      lockDuration,
      compensationType,
      selectedFamilies,
      selectedTerms,
      confirmedFico,
      targetPrice,
    }),
    [lockDuration, compensationType, selectedFamilies, selectedTerms, confirmedFico, targetPrice],
  );

  const runPreview = useCallback(async () => {
    if (!selectedFile) return;
    if (needsFico && !ficoValid) return;

    setLoading(true);
    setError(null);
    try {
      const result = await previewMismoAdvisor({
        file: selectedFile,
        lockTerm: lockDuration,
        compensationType,
        productFamilies: selectedFamilies,
        terms: selectedTerms,
        confirmedFico: confirmedFico ? Number(confirmedFico) : undefined,
        targetPrice: parseTargetPrice(targetPrice),
      }).unwrap();
      setPreview(result);
      setDisagreeMode(false);
      setOverrideRate(null);
    } catch (err) {
      setError(getAxiosErrorMessage(err, 'Failed to analyze MISMO file.'));
    } finally {
      setLoading(false);
    }
  }, [
    selectedFile,
    lockDuration,
    compensationType,
    selectedFamilies,
    selectedTerms,
    confirmedFico,
    targetPrice,
    needsFico,
    ficoValid,
    previewMismoAdvisor,
  ]);

  useEffect(() => {
    if (!selectedFile || preview) return;
    runPreview();
  }, [selectedFile]);

  const recommendedProduct = useMemo(() => {
    if (!hasRecommendation || !preview?.pricingResponse?.products) return null;
    return preview.pricingResponse.products.find(
      (p) => p.productId === recommendation.productId,
    );
  }, [preview, recommendation, hasRecommendation]);

  const handleAgree = () => {
    const rate = disagreeMode && overrideRate
      ? {
        productId: overrideRate.productId || recommendation.productId,
        productShortName: overrideRate.productShortName || recommendation.productShortName,
        productName: overrideRate.productName || recommendation.productName,
        rate: overrideRate.rate,
        adjustedPrice: overrideRate.adjustedPrice,
        lockTerm: overrideRate.lockTerm || lockDuration,
        quoteId: overrideRate.quoteId,
      }
      : {
        productId: recommendation.productId,
        productShortName: recommendation.productShortName,
        productName: recommendation.productName,
        rate: recommendation.rate,
        adjustedPrice: recommendation.adjustedPrice,
        lockTerm: recommendation.lockTerm || lockDuration,
        quoteId: recommendation.quoteId,
      };
    onAgree({
      preview,
      advisorOptions,
      assignPayload: {
        pricingRequest: preview.pricingRequest,
        productId: rate.productId,
        rate: rate.rate,
        price: rate.adjustedPrice,
        lockTerm: rate.lockTerm || lockDuration,
        ...(rate.quoteId ? { quoteId: rate.quoteId } : {}),
        ...(compensationType === 'BPC' && bpcValue
          ? { bpcCalcMethod, bpcValue: parseFloat(bpcValue) }
          : {}),
      },
    });
  };

  const handleDisagree = () => {
    if (!disagreeMode) {
      setDisagreeMode(true);
      return;
    }
    if (!overrideRate) {
      setError('Select a rate from the grid below.');
      return;
    }
    onDisagree({
      preview,
      advisorOptions,
      preferredProductShortName: overrideRate.productShortName,
      preferredRate: overrideRate.rate,
      pricingFilters: {
        lockDuration,
        compensationType,
        selectedFamilies,
        selectedTerms,
        preferredProductShortName: overrideRate.productShortName,
        preferredRate: overrideRate.rate,
        ...(compensationType === 'BPC' ? { bpcCalcMethod, bpcValue } : {}),
      },
    });
  };

  const busy = loading || parentLoading;
  const showPricingInputs = !loading && !!preview;

  return (
    <>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>{ADVISOR_DISCLAIMER}</Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {showPricingInputs && (
          <Box sx={{ mb: 2 }}>
            {needsFico && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Credit score was not found in the MISMO file. Enter representative FICO to run pricing.
                </Typography>
                <TextField
                  label="Representative FICO"
                  type="number"
                  size="small"
                  fullWidth
                  value={confirmedFico}
                  onChange={(e) => setConfirmedFico(e.target.value)}
                  inputProps={{ min: 300, max: 850 }}
                  helperText="300–850"
                  sx={{ mb: 1.5 }}
                />
              </>
            )}
            <TextField
              label="Target price (points)"
              type="number"
              size="small"
              fullWidth
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              placeholder="100.000"
              helperText="Optional — picks the rate closest to this price (100 = par)"
              sx={{ mb: 1 }}
            />
            <Button
              variant="contained"
              disabled={!ficoValid}
              onClick={runPreview}
            >
              {hasRecommendation ? 'Update recommendation' : 'Analyze loan'}
            </Button>
          </Box>
        )}

        {loading && (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Analyzing your MISMO file…
            </Typography>
          </Stack>
        )}

        {analysis && !loading && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <AutoAwesomeIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={700}>
                  We analyzed the data in your loan file
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {analysis.borrowerName} · {analysis.loanPurpose} · {analysis.propertySummary}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Loan amount</Typography>
                  <Typography variant="body2" fontWeight={600}>{fmtDollar(analysis.loanAmount)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">LTV</Typography>
                  <Typography variant="body2" fontWeight={600}>{analysis.ltvPercent?.toFixed(1)}%</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Income / debts</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {fmtDollar(analysis.monthlyIncome)} / {fmtDollar(analysis.monthlyDebts)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Est. back-end DTI</Typography>
                  <Typography variant="body2" fontWeight={600}>{analysis.previewBackEndDtiPercent?.toFixed(1)}%</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {hasRecommendation && !loading && (
          <Card sx={{ mb: 2, borderColor: 'primary.main', borderWidth: 1, borderStyle: 'solid' }}>
            <CardContent>
              <Typography variant="overline" color="primary">Recommended program</Typography>
              <Typography variant="h6" fontWeight={700}>{recommendation.productName}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {recommendation.family} · {recommendation.term}-year
              </Typography>
              <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ mb: 0.5 }}>
                {fmtPricingPct(recommendation.rate)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Price {recommendation.adjustedPrice?.toFixed(3)} pts
                {recommendation.monthlyPayment > 0 && (
                  <> · Est. payment {fmtDollar(recommendation.monthlyPayment)}/mo</>
                )}
              </Typography>
              {pricingQuotedAt && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Pricing last updated {fmtDateTime(pricingQuotedAt)}
                </Typography>
              )}
              <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                {(recommendation.rationale || []).map((line, i) => (
                  <Typography key={i} component="li" variant="body2" color="text.secondary">{line}</Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        {!hasRecommendation && preview && !needsFico && !loading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No eligible product matched this scenario.
            {preview.coachingTips?.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {preview.coachingTips.map((t, i) => (
                  <li key={i}><Typography variant="body2">{t}</Typography></li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {disagreeMode && recommendedProduct && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Choose a different rate for {recommendedProduct.productName}
            </Typography>
            <RateGrid
              product={recommendedProduct}
              loanAmount={analysis?.loanAmount || 0}
              selectedRate={overrideRate}
              onSelectRate={setOverrideRate}
              showAllRates={showAllRates}
              onToggleShowAll={() => setShowAllRates((v) => !v)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button color="inherit" onClick={onBack} disabled={busy}>Back</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<ThumbDownIcon />}
          onClick={handleDisagree}
          disabled={busy || (!hasRecommendation && !disagreeMode)}
        >
          {disagreeMode ? 'Continue with my rate' : 'I disagree'}
        </Button>
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <ThumbUpIcon />}
          onClick={handleAgree}
          disabled={busy || !hasRecommendation || (disagreeMode && !overrideRate)}
        >
          {busy ? 'Working…' : 'I agree — use this product & rate'}
        </Button>
      </DialogActions>
    </>
  );
};

export default MismoAdvisorStep;
export { ADVISOR_DISCLAIMER };
